import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import { User, Menu, X } from 'lucide-react';
import Dashboard from '../components/sales/Dashboard';
import CreateOrder from '../components/sales/CreateOrder';
import CustomOrder from '../components/sales/CustomOrder';
import Orders from '../components/sales/Orders';
import Spiffs from '../components/sales/Spiffs';
import Pricing from '../components/sales/Pricing';
import MyAccount from '../components/sales/MyAccount';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const SalesDashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', path: '/app' },
    { id: 'create', label: 'Create Order', path: '/app/create' },
    { id: 'orders', label: 'Orders', path: '/app/orders' },
    { id: 'spiffs', label: 'Spiffs', path: '/app/spiffs' },
    { id: 'pricing', label: 'Pricing', path: '/app/pricing' },
  ];

  useEffect(() => {
    const path = window.location.pathname;
    const tab = tabs.find(t => t.path === path);
    if (tab) setActiveTab(tab.id);
  }, [window.location.pathname]);

  return (
    <div>
      <header className="header">
        <div className="header-content">
          <Link to="/app" className="header-logo">
            <img src="/logo.png" alt="InstallNow" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="desktop-nav" style={{ display: 'flex', gap: '4px' }}>
            {tabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '10px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '14px',
                  background: activeTab === tab.id ? '#FDB515' : 'transparent',
                  color: activeTab === tab.id ? '#000' : '#666',
                  transition: 'all 0.2s ease'
                }}
                data-testid={`nav-${tab.id}`}
              >
                {tab.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Actions */}
          <div className="header-actions desktop-actions">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="btn btn-secondary" data-testid="account-menu-trigger">
                  <User size={16} />
                  My Account
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => navigate('/app/account')} data-testid="account-menu-profile">
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={onLogout} data-testid="account-menu-logout">
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Mobile Hamburger Button */}
          <button
            className="mobile-menu-button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            style={{
              display: 'none',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#1A1A1A'
            }}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {mobileMenuOpen && (
          <div className="mobile-nav" style={{
            display: 'none',
            flexDirection: 'column',
            background: 'white',
            borderTop: '1px solid rgba(0, 0, 0, 0.1)',
            padding: '16px'
          }}>
            {tabs.map(tab => (
              <Link
                key={tab.id}
                to={tab.path}
                onClick={() => {
                  setActiveTab(tab.id);
                  setMobileMenuOpen(false);
                }}
                style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  textDecoration: 'none',
                  fontWeight: '600',
                  fontSize: '16px',
                  background: activeTab === tab.id ? '#FDB515' : 'transparent',
                  color: activeTab === tab.id ? '#000' : '#666',
                  marginBottom: '4px'
                }}
                data-testid={`mobile-nav-${tab.id}`}
              >
                {tab.label}
              </Link>
            ))}
            <div style={{ borderTop: '1px solid #E0E0E0', margin: '12px 0' }} />
            <button
              onClick={() => {
                navigate('/app/account');
                setMobileMenuOpen(false);
              }}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '16px',
                color: '#666',
                cursor: 'pointer'
              }}
            >
              My Account
            </button>
            <button
              onClick={() => {
                onLogout();
                setMobileMenuOpen(false);
              }}
              style={{
                padding: '12px 16px',
                background: 'transparent',
                border: 'none',
                textAlign: 'left',
                fontWeight: '600',
                fontSize: '16px',
                color: '#EF4444',
                cursor: 'pointer'
              }}
            >
              Logout
            </button>
          </div>
        )}
      </header>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '32px 24px' }}>
        <Routes>
          <Route path="/" element={<Dashboard user={user} />} />
          <Route path="/create" element={<CreateOrder user={user} />} />
          <Route path="/create-custom" element={<CustomOrder user={user} />} />
          <Route path="/orders" element={<Orders user={user} />} />
          <Route path="/spiffs" element={<Spiffs user={user} />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/account" element={<MyAccount user={user} />} />
        </Routes>
      </main>
    </div>
  );
};

export default SalesDashboard;
